// src/context/AppContext.jsx  (Phase 2 — Supabase)

import React, {
  createContext, useContext, useState,
  useEffect, useCallback,
} from 'react'
import { supabase }                    from '../lib/supabase'
import { getSession, signIn, signOut, signUp } from '../services/authService'
import {
  fetchAccounts, createAccount,
  updateAccount, deleteAccount,
} from '../services/accountService'
import {
  fetchTransactions, createTransaction,
  updateTransaction, deleteTransaction,
  getNextRef,
} from '../services/transactionService'
import { uid } from '../utils/helpers'
import { canAddTransaction, hasFullReports, getPlan } from '../services/billingService'

const AppContext = createContext(null)

export function AppProvider({ children }) {

  const [currentUser,    setCurrentUser]    = useState(null)
  const [currentProfile, setCurrentProfile] = useState(null)
  const [authLoading,    setAuthLoading]    = useState(true)
  const [accounts,       setAccounts]       = useState([])
  const [transactions,   setTransactions]   = useState([])
  const [dataLoading,    setDataLoading]    = useState(false)
  const [toasts,         setToasts]         = useState([])

  const toast = useCallback((msg, type = 'success') => {
    const id = uid()
    setToasts(prev => [...prev, { id, msg, type }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000)
  }, [])

  const toastError = useCallback((msg) => toast(msg, 'error'), [toast])

  // Load session on mount
  useEffect(() => {
    ;(async () => {
      const { user, profile } = await getSession()
      setCurrentUser(user)
      setCurrentProfile(profile)
      setAuthLoading(false)
    })()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setCurrentUser(session.user)
          const { data } = await supabase
            .from('profiles').select('*').eq('id', session.user.id).single()
          setCurrentProfile(data)
        } else {
          setCurrentUser(null); setCurrentProfile(null)
          setAccounts([]); setTransactions([])
        }
        setAuthLoading(false)
      }
    )
    return () => subscription.unsubscribe()
  }, [])

  // Load data when user logs in
  useEffect(() => {
    if (!currentUser) return
    loadData()
  }, [currentUser])

  const loadData = async () => {
    setDataLoading(true)
    const [ar, tr] = await Promise.all([fetchAccounts(), fetchTransactions()])
    if (!ar.error) setAccounts(ar.data)
    if (!tr.error) setTransactions(tr.data)
    setDataLoading(false)
  }

  // Auth
  const login = useCallback(async (email, password) => {
    const { user, profile, error } = await signIn(email, password)
    if (error) return { error }
    setCurrentUser(user); setCurrentProfile(profile)
    return { error: null }
  }, [])

  // Public self-signup — always creates role:'user', never admin
  const register = useCallback(async (name, email, password) => {
    const initials = name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
    const colors = ['#1c3a5e', '#0a6640', '#a32d2d', '#854f0b', '#5e35b1', '#0f6e56']
    const color = colors[Math.floor(Math.random() * colors.length)]

    const { user, error } = await signUp(email, password, {
      name: name.trim(),
      role: 'user',
      color,
      initials,
    })
    if (error) return { error }

    // Supabase may require email confirmation depending on project settings.
    // If a session was returned immediately, log them straight in.
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) {
      setCurrentUser(session.user)
      const { data: profile } = await supabase
        .from('profiles').select('*').eq('id', session.user.id).single()
      setCurrentProfile(profile)
      return { error: null, needsConfirmation: false }
    }
    return { error: null, needsConfirmation: true }
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    setCurrentUser(null); setCurrentProfile(null)
    setAccounts([]); setTransactions([])
  }, [])

  // Refresh accounts helper
  const refreshAccounts = async () => {
    const { data } = await fetchAccounts()
    if (data) setAccounts(data)
  }

  // Accounts
  const addAccount = useCallback(async (data) => {
    const { data: a, error } = await createAccount(data)
    if (error) { toastError('Failed to add account: ' + error); return null }
    setAccounts(prev => [...prev, a]); toast('Account added!')
    return a
  }, [toast, toastError])

  const editAccount = useCallback(async (id, data) => {
    const { data: u, error } = await updateAccount(id, data)
    if (error) { toastError('Failed to update account: ' + error); return }
    setAccounts(prev => prev.map(a => a.id === id ? u : a)); toast('Account updated!')
  }, [toast, toastError])

  const removeAccount = useCallback(async (id) => {
    const { error } = await deleteAccount(id)
    if (error) { toastError('Failed to delete account: ' + error); return }
    setAccounts(prev => prev.filter(a => a.id !== id)); toast('Account deleted')
  }, [toast, toastError])

  // Transactions
  const addTransaction = useCallback(async (data) => {
    const gate = canAddTransaction(currentProfile, transactions.length)
    if (!gate.allowed) {
      toastError(gate.reason)
      return { limitReached: true }
    }
    const ref = await getNextRef()
    const { data: t, error } = await createTransaction({ ...data, ref })
    if (error) { toastError('Failed to save: ' + error); return null }
    setTransactions(prev => [t, ...prev])
    await refreshAccounts()
    toast('Transaction saved!')
    return t
  }, [transactions, currentProfile, toast, toastError])

  const editTransaction = useCallback(async (id, newData) => {
    const old = transactions.find(t => t.id === id)
    if (!old) return
    const { data: u, error } = await updateTransaction(id, old, newData)
    if (error) { toastError('Failed to update: ' + error); return }
    setTransactions(prev => prev.map(t => t.id === id ? u : t))
    await refreshAccounts()
    toast('Transaction updated!')
  }, [transactions, toast, toastError])

  const removeTransaction = useCallback(async (id) => {
    const txn = transactions.find(t => t.id === id)
    if (!txn) return
    const { error } = await deleteTransaction(id, txn)
    if (error) { toastError('Failed to delete: ' + error); return }
    setTransactions(prev => prev.filter(t => t.id !== id))
    await refreshAccounts()
    toast('Transaction deleted')
  }, [transactions, toast, toastError])

  // Helpers
  const acctName  = useCallback((id) => accounts.find(a => a.id === id)?.name  || '—', [accounts])
  const acctColor = useCallback((id) => accounts.find(a => a.id === id)?.color || '#888', [accounts])

  const totalIncome  = transactions.filter(t => t.type === 'income') .reduce((s, t) => s + t.amt, 0)
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amt, 0)
  const netSavings   = totalIncome - totalExpense
  const totalAssets  = accounts.filter(a => a.balance > 0).reduce((s, a) => s + a.balance, 0)
  const totalLiab    = accounts.filter(a => a.balance < 0).reduce((s, a) => s + Math.abs(a.balance), 0)

  // Plan / billing derived state
  const currentPlan      = getPlan(currentProfile?.plan || 'free')
  const txnCount         = transactions.length
  const txnLimitReached  = currentPlan.txnLimit !== Infinity && txnCount >= currentPlan.txnLimit
  const txnRemaining     = currentPlan.txnLimit === Infinity ? Infinity : Math.max(0, currentPlan.txnLimit - txnCount)
  const userHasFullReports = hasFullReports(currentProfile)

  const refreshProfile = useCallback(async () => {
    if (!currentUser) return
    const { data } = await supabase.from('profiles').select('*').eq('id', currentUser.id).single()
    if (data) setCurrentProfile(data)
  }, [currentUser])

  return (
    <AppContext.Provider value={{
      currentUser, currentProfile, authLoading, dataLoading,
      login, logout, register, loadData,
      accounts, addAccount, editAccount, removeAccount,
      transactions, addTransaction, editTransaction, removeTransaction,
      acctName, acctColor,
      totalIncome, totalExpense, netSavings, totalAssets, totalLiab,
      currentPlan, txnCount, txnLimitReached, txnRemaining, userHasFullReports, refreshProfile,
      toast, toastError, toasts,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be inside AppProvider')
  return ctx
}
